import { useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'worksheet-editor-split-ratio';
const DEFAULT_LEFT_PERCENT = 28;
const MIN_SIDEBAR_PX = 260;
const MIN_CANVAS_PX = 520;

function getSavedSizes(): [number, number] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as unknown;
      if (
        Array.isArray(parsed) &&
        parsed.length === 2 &&
        typeof parsed[0] === 'number' &&
        typeof parsed[1] === 'number' &&
        parsed[0] >= 15 &&
        parsed[0] <= 60
      ) {
        return [parsed[0], parsed[1]];
      }
    }
  } catch {
    /* localStorage unavailable or invalid */
  }
  return [DEFAULT_LEFT_PERCENT, 100 - DEFAULT_LEFT_PERCENT];
}

function saveSizes(sizes: number[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes));
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Persisted split sizes for the worksheet editor's sidebar/canvas layout.
 * Mirrors the useResizableLayout hook used by StudyDetail.
 */
export function useEditorResizableLayout() {
  const [sizes, setSizes] = useState<[number, number]>(getSavedSizes);

  const handleDragEnd = useCallback((newSizes: number[]) => {
    const left = Math.round(newSizes[0]);
    const right = 100 - left;
    setSizes([left, right]);
    saveSizes([left, right]);
  }, []);

  const gutterStyle = useCallback(
    (_dimension: string, gutterSize: number) => ({
      background: 'var(--bg-elevated)',
      cursor: 'col-resize',
      width: `${gutterSize}px`,
      borderLeft: '1px solid var(--border)',
      borderRight: '1px solid var(--border)',
    }),
    []
  );

  const splitProps = useMemo(
    () => ({
      sizes,
      minSize: [MIN_SIDEBAR_PX, MIN_CANVAS_PX] as [number, number],
      gutterSize: 6,
      snapOffset: 0,
      direction: 'horizontal' as const,
      cursor: 'col-resize' as const,
      gutterStyle,
      onDragEnd: handleDragEnd,
    }),
    [sizes, gutterStyle, handleDragEnd]
  );

  return { splitProps };
}
